// Desktop Menu with Full-Width Slide-in Functionality
class DesktopMenu {
  constructor() {
    this.menuContainer = document.querySelector('[data-desktop-menu-container]');
    this.menuTriggers = document.querySelectorAll('[data-desktop-menu-trigger]');
    this.menuContents = document.querySelectorAll('.desktop-menu-content');
    this.header = document.getElementById('site-header');
    this.logo = document.getElementById('logo');
    this.overlay = document.getElementById('desktop-menu-overlay');

    this.isOpen = false;
    this.currentMenuIndex = null;
    this.currentTriggerMode = null;
    this.contentTimeout = null;
    this.closeTimeout = null;
    this.isTransitioning = false;

    this.init();
  }

  init() {
    if (!this.menuContainer || !this.menuTriggers.length) return;

    this.bindEvents();
  }

  bindEvents() {
    this.menuTriggers.forEach((trigger) => {
      const menuIndex = trigger.dataset.menuIndex;
      const isClickMode = trigger.dataset.triggerMode === 'click';

      if (isClickMode) {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.handleMenuClick(menuIndex);
        });
      } else {
        trigger.addEventListener('mouseenter', () => this.handleMenuHover(menuIndex));
        trigger.addEventListener('focus', () => this.handleMenuHover(menuIndex));
        trigger.addEventListener('mouseleave', () => this.scheduleClose());
        trigger.addEventListener('blur', () => this.scheduleClose());
      }
    });

    // Stay open while hovering the panel itself, close when leaving it
    // (click-triggered panels stay open regardless of hover)
    this.menuContainer.addEventListener('mouseenter', () => this.clearCloseTimeout());
    this.menuContainer.addEventListener('mouseleave', () => {
      if (this.currentTriggerMode !== 'click') this.scheduleClose();
    });

    // Close click-triggered panels when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.isOpen || this.currentTriggerMode !== 'click') return;
      const clickedTrigger = e.target.closest('[data-desktop-menu-trigger]');
      const clickedContainer = e.target.closest('[data-desktop-menu-container]');
      if (!clickedTrigger && !clickedContainer) this.hideMenu();
    });

    // ESC key to close menu
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.hideMenu();
      }
    });
  }

  handleMenuHover(menuIndex) {
    this.clearCloseTimeout();
    if (this.isOpen && this.currentMenuIndex === menuIndex) return;
    this.showMenu(menuIndex, 'hover');
  }

  handleMenuClick(menuIndex) {
    this.clearCloseTimeout();
    if (this.isOpen && this.currentMenuIndex === menuIndex) {
      this.hideMenu();
      return;
    }
    this.showMenu(menuIndex, 'click');

    const targetContent = document.querySelector(`[data-menu-content="${menuIndex}"]`);
    targetContent?.querySelector('[data-search-input]')?.focus();
  }

  scheduleClose() {
    this.clearCloseTimeout();
    this.closeTimeout = setTimeout(() => this.hideMenu(), 100);
  }

  clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  clearTimeouts() {
    if (this.contentTimeout) {
      clearTimeout(this.contentTimeout);
      this.contentTimeout = null;
    }
  }

  clearSearchInputs(skipValueClear = false) {
    this.menuContainer.querySelectorAll('[data-search-input]').forEach(input => {
      if (!skipValueClear) input.value = '';
      const dropdown = input.closest('[data-search-form]')?.querySelector('[data-search-autocomplete]');
      if (dropdown) {
        dropdown.classList.remove('is-visible');
        dropdown.innerHTML = '';
      }
    });
    this.menuContainer.querySelectorAll('.desktop-menu-content > ul').forEach(el => {
      el.style.visibility = '';
    });
  }

  showMenu(menuIndex, triggerMode = 'hover') {
    // Clear any existing timeouts
    this.clearTimeouts();

    const targetContent = document.querySelector(`[data-menu-content="${menuIndex}"]`);
    if (!targetContent) return;

    this.currentTriggerMode = triggerMode;

    // Update aria-expanded for all triggers
    this.menuTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', trigger.dataset.menuIndex === menuIndex ? 'true' : 'false');
    });

    // If menu is already open but switching content
    if (this.isOpen && this.currentMenuIndex !== menuIndex) {
      this.clearSearchInputs();

      // Hide all content immediately
      this.menuContents.forEach((content) => {
        if (content.dataset.menuContent !== menuIndex) {
          content.classList.remove('is-visible');
        }
      });

      // Show new content immediately
      targetContent.classList.add('is-visible');
      this.currentMenuIndex = menuIndex;

      return;
    }

    // Initial menu opening
    if (!this.isOpen) {
      this.isTransitioning = true;

      // Clear search inputs from previous session
      this.menuContainer.querySelectorAll('[data-search-input]').forEach(input => {
        input.value = '';
        const dropdown = input.closest('[data-search-form]')?.querySelector('[data-search-autocomplete]');
        if (dropdown) {
          dropdown.classList.remove('is-visible');
          dropdown.innerHTML = '';
        }
      });
      this.menuContainer.querySelectorAll('.desktop-menu-content > ul').forEach(el => {
        el.style.visibility = '';
      });

      // Hide all content initially
      this.menuContents.forEach(content => {
        content.classList.remove('is-visible');
      });

      // Show target content
      targetContent.classList.add('is-visible');
      
      // Trigger slide-in animation
      this.menuContainer.classList.add('is-open');
      
      // Add menu-open class to header for styling
      this.header.classList.add('menu-open');

      // Lock body scroll
      document.body.style.overflow = 'hidden';

      // Show overlay
      if (this.overlay) {
        this.overlay.classList.remove('opacity-0', 'pointer-events-none');
        this.overlay.classList.add('opacity-50', 'pointer-events-auto');
      }

      // Content is already visible, just mark as not transitioning
      this.contentTimeout = setTimeout(() => {
        this.isTransitioning = false;
      }, 75);
      
      this.isOpen = true;
      this.currentMenuIndex = menuIndex;
    }
  }

  hideMenu() {
    if (!this.isOpen) return;

    // Clear any pending timeouts
    this.clearTimeouts();
    this.isTransitioning = true;

    // Update aria-expanded for all triggers
    this.menuTriggers.forEach(trigger => {
      trigger.setAttribute('aria-expanded', 'false');
    });

    // Hide overlay
    if (this.overlay) {
      this.overlay.classList.remove('opacity-50', 'pointer-events-auto');
      this.overlay.classList.add('opacity-0', 'pointer-events-none');
    }

    // Trigger slide-out animation AND header color change simultaneously
    this.menuContainer.classList.remove('is-open');
    this.header.classList.remove('menu-open');

    // Fade out content while sliding (simultaneous animations)
    this.menuContents.forEach(content => {
      content.classList.remove('is-visible');
    });
    
    // Unlock body scroll
    document.body.style.overflow = '';

    // Clear search inputs, hide autocomplete, restore menu links
    this.menuContainer.querySelectorAll('.desktop-menu-content > ul').forEach(el => {
      el.style.visibility = '';
    });
    this.menuContainer.querySelectorAll('[data-search-input]').forEach(input => {
      const form = input.closest('[data-search-form]');
      // Don't clear if a form submit is in progress (value needed for URL)
      if (!form?.dataset.submitting) input.value = '';
      const dropdown = form?.querySelector('[data-search-autocomplete]');
      if (dropdown) {
        dropdown.classList.remove('is-visible');
        dropdown.innerHTML = '';
      }
    });

    // Clean up after slide animation completes
    this.contentTimeout = setTimeout(() => {
      this.isTransitioning = false;
    }, 150); // Wait for slide animation to complete

    this.isOpen = false;
    this.currentMenuIndex = null;
    this.currentTriggerMode = null;
  }

}

// Initialize desktop menu when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.desktopMenu = new DesktopMenu();
});
